/**
 * ChurchTeamRolesTab — per-church member RBAC.
 *
 * Two sub-sections:
 *   1) Role catalogue — Pastor, Elder, Deacon, Treasurer… each with a list
 *      of permission keys. Admin can edit/add/delete.
 *   2) Team — who holds which role in this church. Add/remove assignments.
 *
 * Pairs with `/api/church/member-rbac/{church_id}` endpoints.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, Plus, Trash2, Loader2, UserPlus, Edit3, Lock, RefreshCw, ShieldAlert, FileText, Check, X } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${r.status})`);
  }
  return r.json();
};

export default function ChurchTeamRolesTab({ churchId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
    queryKey: ['church-roles', churchId],
    queryFn: () => api(`/api/church/member-rbac/${churchId}/roles`),
    retry: false,
  });
  const roles = rolesData?.roles || [];
  const catalogue = rolesData?.permission_catalogue || [];
  const groups = rolesData?.permission_groups || [];

  const { data: assignData, isLoading: assignLoading } = useQuery({
    queryKey: ['church-role-assignments', churchId],
    queryFn: () => api(`/api/church/member-rbac/${churchId}/assignments`),
  });
  const assignments = assignData?.assignments || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['church-roles', churchId] });
    qc.invalidateQueries({ queryKey: ['church-role-assignments', churchId] });
    qc.invalidateQueries({ queryKey: ['church-members'] });
    qc.invalidateQueries({ queryKey: ['church-members-picker', churchId] });
    qc.invalidateQueries({ queryKey: ['member-roles'] });
    qc.invalidateQueries({ queryKey: ['member-360'] });
  };

  const delRole = useMutation({
    mutationFn: (id) => api(`/api/church/member-rbac/${churchId}/roles/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Role deleted'); invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const delAssign = useMutation({
    mutationFn: (id) => api(`/api/church/member-rbac/${churchId}/assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  if (rolesError) {
    return (
      <Card data-testid="church-roles-forbidden">
        <CardContent className="py-12 text-center space-y-2">
          <ShieldAlert className="h-8 w-8 mx-auto text-amber-500" />
          <p className="text-sm text-slate-600">Only this church's Admin or a Super Admin can manage roles and permissions.</p>
          <p className="text-xs text-slate-400">{rolesError.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="church-team-roles-tab">
      {/* Team list */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-700" />Team ({assignments.length})</CardTitle>
          <Button size="sm" onClick={() => setAssigning(true)} data-testid="team-assign-btn"><UserPlus className="h-4 w-4 mr-1" />Assign a member</Button>
        </CardHeader>
        <CardContent className="p-0">
          {assignLoading ? (
            <div className="p-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : assignments.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">No team members assigned yet. Click "Assign a member" to set Pastors, Elders, Ushers…</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Member</th>
                <th className="text-left py-2 px-3">Role</th>
                <th className="text-left py-2 px-3">Permissions</th>
                <th className="text-right py-2 px-3"></th>
              </tr></thead>
              <tbody>
                {assignments.map(a => (
                  <tr key={a.id} className="border-b last:border-0" data-testid={`team-row-${a.id}`}>
                    <td className="py-2 px-3">
                      <p className="font-medium text-slate-700">{a.member_name || '—'}</p>
                      <p className="text-[10px] text-slate-400">{a.member_email || ''}</p>
                    </td>
                    <td className="py-2 px-3"><Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{a.role_name}</Badge></td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {(a.permissions || []).slice(0, 4).map(p => (
                          <span key={p} className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{p}</span>
                        ))}
                        {(a.permissions || []).length > 4 && <span className="text-[9px] text-slate-400">+{a.permissions.length - 4}</span>}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm('Remove this assignment?')) delAssign.mutate(a.id); }} data-testid={`team-remove-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <TeamBiosCard churchId={churchId} />

      {/* Role catalogue */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Roles &amp; permissions ({roles.length})</CardTitle>
            <p className="text-[11px] text-slate-400 mt-0.5">Built-in roles can be re-permissioned; custom roles can be renamed or deleted. Scoped to this church.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing({})} data-testid="roles-add"><Plus className="h-4 w-4 mr-1" />New role</Button>
        </CardHeader>
        <CardContent>
          {rolesLoading ? (
            <div className="p-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
          ) : roles.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Default catalogue will seed automatically — reload the tab.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {roles.map(r => (
                <div key={r.id} className="border rounded-lg p-3 bg-white" data-testid={`role-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="font-medium text-slate-700">{r.name}
                        {r.is_global ? (
                          <span className="ml-2 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700"
                                data-testid={`role-global-badge-${r.id}`}>
                            <Lock className="h-2.5 w-2.5" /> PLATFORM CUSTOM
                          </span>
                        ) : r.is_system && (
                          <span className="ml-2 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
                                data-testid={`role-system-badge-${r.id}`}>
                            <Lock className="h-2.5 w-2.5" /> BUILT-IN
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-6 w-6 disabled:text-slate-300"
                              disabled={r.is_global}
                              title={r.is_global ? 'Set centrally by a Super Admin under Church Roles' : 'Edit role'}
                              onClick={() => setEditing(r)} data-testid={`role-edit-${r.id}`}><Edit3 className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" disabled={r.is_system}
                               title={r.is_global ? 'Platform roles are withdrawn by a Super Admin'
                                 : r.is_system ? 'Built-in roles cannot be deleted' : 'Delete role'}
                               className="h-6 w-6 text-rose-600 disabled:text-slate-300"
                               onClick={() => { if (window.confirm(`Delete role "${r.name}" and all its assignments?`)) delRole.mutate(r.id); }}
                               data-testid={`role-delete-${r.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(r.permissions || []).map(p => (
                      <span key={p} className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{p}</span>
                    ))}
                    {(r.permissions || []).length === 0 && <span className="text-[10px] text-slate-400 italic">no permissions</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editing && <RoleDialog churchId={churchId} role={editing} catalogue={catalogue} groups={groups} onClose={() => setEditing(null)} onSaved={() => { invalidate(); setEditing(null); }} />}
      {assigning && <AssignDialog churchId={churchId} roles={roles} onClose={() => setAssigning(false)} onSaved={() => { invalidate(); setAssigning(false); }} />}
    </div>
  );
}

/* ── Team bios: what the public church page shows ──────────────────── */

const BIO_STATUS = {
  none: { label: 'No bio yet', chip: 'bg-slate-50 text-slate-500 border-slate-200' },
  pending: { label: 'Waiting for approval', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Published', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Sent back', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function TeamBiosCard({ churchId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['church-team-bios', churchId],
    queryFn: () => api(`/api/church/${churchId}/team-bios`),
    retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['church-team-bios', churchId] });

  const approve = useMutation({
    mutationFn: (memberId) => api(`/api/church/${churchId}/team-bios/${memberId}/approve`, { method: 'POST' }),
    onSuccess: () => { toast.success('Bio published to the church page'); refresh(); },
    onError: (e) => toast.error(e.message),
  });
  const reject = useMutation({
    mutationFn: ({ memberId, note }) => api(`/api/church/${churchId}/team-bios/${memberId}/reject`,
      { method: 'POST', body: JSON.stringify({ note }) }),
    onSuccess: () => { toast.success('Sent back with your note'); refresh(); },
    onError: (e) => toast.error(e.message),
  });

  if (error) return null;
  const items = data?.items || [];

  return (
    <Card data-testid="team-bios-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-600" /> Public bios
            {data?.pending_count > 0 && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                     data-testid="team-bios-pending-count">
                {data.pending_count} to review
              </Badge>
            )}
          </CardTitle>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Leaders write their own bio in the member portal — it appears on the church page once you approve it.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <div className="p-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-400" data-testid="team-bios-empty">
            Assign members to a church role first — their bios then show up here for review.
          </p>
        ) : items.map((m) => (
          <div key={m.member_id} className="rounded-lg border border-slate-200 p-3"
               data-testid={`team-bio-row-${m.member_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                {(m.photo_url || m.draft_photo_url || m.member_photo_url) ? (
                  <img src={m.draft_photo_url || m.photo_url || m.member_photo_url} alt={m.name}
                       className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                    {m.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">{m.name}</p>
                  <p className="text-[11px] text-slate-400">{(m.roles || []).join(' · ')}</p>
                  <Badge variant="outline" className={`mt-1 text-[10px] ${BIO_STATUS[m.status]?.chip}`}
                         data-testid={`team-bio-status-${m.member_id}`}>
                    {BIO_STATUS[m.status]?.label || m.status}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-1.5">
                {m.status === 'pending' && (
                  <>
                    <Button size="sm" onClick={() => approve.mutate(m.member_id)}
                            disabled={approve.isPending}
                            data-testid={`team-bio-approve-${m.member_id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" disabled={reject.isPending}
                            onClick={() => {
                              const note = window.prompt('What should they change?') || '';
                              reject.mutate({ memberId: m.member_id, note });
                            }}
                            data-testid={`team-bio-reject-${m.member_id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Send back
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => setEditing(m)}
                        data-testid={`team-bio-edit-${m.member_id}`}>
                  <Edit3 className="h-3.5 w-3.5 mr-1" /> {m.is_published ? 'Edit' : 'Write'}
                </Button>
              </div>
            </div>
            {m.status === 'pending' && m.draft_bio && (
              <p className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900"
                 data-testid={`team-bio-draft-${m.member_id}`}>
                <span className="font-semibold">Submitted: </span>{m.draft_bio}
              </p>
            )}
            {m.bio && (
              <p className="mt-2 text-xs text-slate-600" data-testid={`team-bio-live-${m.member_id}`}>
                <span className="font-semibold text-slate-500">Live: </span>{m.bio}
              </p>
            )}
            {m.status === 'rejected' && m.review_note && (
              <p className="mt-2 text-[11px] text-rose-600">Your note: {m.review_note}</p>
            )}
          </div>
        ))}
      </CardContent>
      {editing && (
        <BioEditDialog churchId={churchId} member={editing} maxLength={data?.max_length || 600}
                       onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />
      )}
    </Card>
  );
}

function BioEditDialog({ churchId, member, maxLength, onClose, onSaved }) {
  const [bio, setBio] = useState(member.draft_bio || member.bio || '');
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(member.photo_url || '');

  const save = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/team-bios/${member.member_id}`, {
      method: 'PUT', body: JSON.stringify({ bio, photo_url: photoUrl }),
    }),
    onSuccess: () => { toast.success('Bio published'); onSaved(); },
    onError: (e) => toast.error(e.message),
  });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const r = await fetch(`${API_URL}/api/church/${churchId}/team-bios/${member.member_id}/photo`, {
        method: 'POST', headers: { ...authHeaders() }, body,
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Upload failed');
      const res = await r.json();
      setPhotoUrl(res.photo_url);
      toast.success('Photo published');
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="team-bio-dialog">
        <DialogHeader>
          <DialogTitle>{member.name}'s public bio</DialogTitle>
          <DialogDescription>
            Saving here publishes straight to the church page — no approval needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {photoUrl ? (
              <img src={photoUrl} alt={member.name} className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <span className="grid h-16 w-16 place-items-center rounded-full bg-slate-100 text-sm font-semibold text-slate-500">
                {member.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
              </span>
            )}
            <label className="cursor-pointer text-xs font-medium text-indigo-600 hover:underline">
              {uploading ? 'Uploading…' : 'Upload photo'}
              <input type="file" accept="image/*" className="hidden"
                     data-testid="team-bio-photo-input"
                     onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea rows={6} value={bio} maxLength={maxLength} data-testid="team-bio-textarea"
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="A sentence or two about their ministry and family." />
            <p className="mt-1 text-right text-[11px] text-slate-400">{bio.length}/{maxLength}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}
                  data-testid="team-bio-save-btn">
            {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleDialog({ churchId, role, catalogue, groups = [], onClose, onSaved }) {
  const editing = !!role?.id;
  const [name, setName] = useState(role?.name || '');
  const [perms, setPerms] = useState(role?.permissions || []);
  const [q, setQ] = useState('');
  const [openGroups, setOpenGroups] = useState(
    () => new Set((groups.filter(g => g.open).map(g => g.module)).concat(['church_workspace'])));
  const known = new Set(groups.flatMap(g => g.permissions.map(p => p.key)));
  const extras = (role?.extra_permissions || []).filter(p => perms.includes(p) && !known.has(p));

  const toggle = (key) => setPerms(p => (p.includes(key) ? p.filter(x => x !== key) : [...p, key]));
  const toggleGroup = (mod) => setOpenGroups(s => {
    const next = new Set(s);
    next.has(mod) ? next.delete(mod) : next.add(mod);
    return next;
  });
  const term = q.trim().toLowerCase();
  const shown = groups
    .map(g => ({ ...g, matches: term
      ? g.permissions.filter(p => p.key.toLowerCase().includes(term) || p.label.toLowerCase().includes(term))
      : g.permissions }))
    .filter(g => g.matches.length > 0);

  const mut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({ name, permissions: perms });
      return editing
        ? api(`/api/church/member-rbac/${churchId}/roles/${role.id}`, { method: 'PUT', body })
        : api(`/api/church/member-rbac/${churchId}/roles`, { method: 'POST', body });
    },
    onSuccess: () => { toast.success(editing ? 'Role updated' : 'Role created'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${role.name}` : 'New role'}</DialogTitle>
          <DialogDescription>
            Roles and permissions apply to this church only — every assembly can have its own structure.
            {role?.is_system && ' This is a built-in role: permissions can change, the role cannot be deleted.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Role name *</Label>
            <Input value={name} onChange={e => setName(e.target.value)}
                   placeholder="e.g. Worship Lead" data-testid="role-name" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Permissions <span className="text-slate-400 font-normal">({perms.length} selected)</span></Label>
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search all permissions…"
                     className="h-8 w-44 text-xs" data-testid="role-perm-search" />
            </div>
            {shown.map(g => {
              const isOpen = openGroups.has(g.module) || !!term;
              const chosen = g.permissions.filter(p => perms.includes(p.key)).length;
              return (
                <div key={g.module} className="rounded-lg border border-slate-200" data-testid={`role-perm-group-${g.module}`}>
                  <button type="button" onClick={() => toggleGroup(g.module)}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                          data-testid={`role-perm-header-${g.module}`}>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-slate-700">{g.label}</span>
                      <span className="block text-[10px] text-slate-400 truncate">{g.hint}</span>
                    </span>
                    <span className="text-[10px] text-slate-500 shrink-0">
                      {chosen > 0 ? `${chosen}/${g.permissions.length}` : g.permissions.length}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="grid sm:grid-cols-2 gap-2 px-3 pb-3">
                      {g.matches.map(p => (
                        <label key={p.key}
                               className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${perms.includes(p.key) ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
                               data-testid={`role-perm-${p.key}`}>
                          <input type="checkbox" className="mt-0.5" checked={perms.includes(p.key)}
                                 onChange={() => toggle(p.key)} />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium text-slate-700">{p.label}</span>
                            <span className="block text-[10px] text-slate-400 truncate">{p.hint}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {groups.length === 0 && (
              <div className="grid sm:grid-cols-2 gap-2">
                {catalogue.map(p => (
                  <label key={p.key}
                         className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${perms.includes(p.key) ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}
                         data-testid={`role-perm-${p.key}`}>
                    <input type="checkbox" className="mt-0.5" checked={perms.includes(p.key)}
                           onChange={() => toggle(p.key)} />
                    <span>
                      <span className="block text-sm font-medium text-slate-700">{p.label}</span>
                      <span className="block text-[10px] text-slate-400">{p.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {extras.length > 0 && (
            <div data-testid="role-extra-permissions">
              <Label className="text-xs text-slate-500">Other saved permissions (kept)</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {extras.map(p => (
                  <span key={p} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                    {p}
                    <button type="button" className="text-slate-400 hover:text-rose-600"
                            onClick={() => toggle(p)} data-testid={`role-extra-remove-${p}`}>×</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!name || mut.isPending} data-testid="role-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ churchId, roles, onClose, onSaved }) {
  const [memberId, setMemberId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [q, setQ] = useState('');

  // Always fresh: a member added seconds ago must be pickable without a refresh.
  const { data: members = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ['church-members-picker', churchId],
    queryFn: () => api(`/api/church/${churchId}/members?limit=2000`),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? members.filter(m => `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''} ${m.phone || ''}`
        .toLowerCase().includes(needle))
    : members;
  const selected = members.find(m => m.id === memberId);

  const mut = useMutation({
    mutationFn: () => api(`/api/church/member-rbac/${churchId}/assignments`, {
      method: 'POST',
      body: JSON.stringify({ member_id: memberId, role_id: roleId }),
    }),
    onSuccess: () => { toast.success('Member assigned'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign a member to a role</DialogTitle>
          <DialogDescription>
            The full active roster of this church, refreshed every time you open this dialog.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label>Member *</Label>
              <button type="button" onClick={() => refetch()}
                      className="text-[10px] text-emerald-700 hover:underline inline-flex items-center gap-1"
                      data-testid="assign-refresh">
                <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} /> Refresh list
              </button>
            </div>
            <Input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Search by name, email or phone…"
                   className="mt-1" data-testid="assign-member-search" />
            <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border divide-y" data-testid="assign-member-list">
              {isLoading ? (
                <p className="p-3 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Loading roster…</p>
              ) : filtered.length === 0 ? (
                <p className="p-3 text-xs text-slate-400">No member matches “{q}”.</p>
              ) : filtered.slice(0, 200).map(m => (
                <button key={m.id} type="button" onClick={() => setMemberId(m.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${memberId === m.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                        data-testid={`assign-member-option-${m.id}`}>
                  <span className="font-medium text-slate-700">{m.first_name} {m.last_name}</span>
                  {m.email && <span className="block text-[10px] text-slate-400">{m.email}</span>}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-slate-400" data-testid="assign-member-count">
              {filtered.length} of {members.length} members{selected ? ` · selected: ${selected.first_name} ${selected.last_name}` : ''}
            </p>
          </div>
          <div><Label>Role *</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger data-testid="assign-role"><SelectValue placeholder="Pick a role" /></SelectTrigger>
              <SelectContent>
                {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!memberId || !roleId || mut.isPending} data-testid="assign-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
